import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, LayoutDashboard, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ProfilePictureUpload } from "@/components/ProfilePictureUpload";
import { invalidateProfiles } from "@/lib/socialBatch";

interface Props {
  userId: string;
  firstName: string;
  /** Dual-role accounts get a shorter flow (photo + bio). */
  short?: boolean;
  initial: { business_avatar_url: string | null; business_bio: string | null; website: string | null; instagram_handle: string | null };
  onDone: () => void;
}

export const BusinessWelcomeWizard = ({ userId, firstName, short, initial, onDone }: Props) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const total = short ? 2 : 3;
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [avatar, setAvatar] = useState<string | null>(initial.business_avatar_url);
  const [bio, setBio] = useState(initial.business_bio || "");
  const [website, setWebsite] = useState(initial.website || "");
  const [instagram, setInstagram] = useState(initial.instagram_handle || "");
  const [websiteError, setWebsiteError] = useState("");

  const save = async (updates: Record<string, unknown>) => {
    setSaving(true);
    const { error } = await supabase.from("profiles").update(updates as any).eq("id", userId);
    setSaving(false);
    if (error) { toast.error("Couldn't save that, please try again"); return false; }
    invalidateProfiles();
    qc.invalidateQueries({ queryKey: ["user-profile", userId] });
    return true;
  };
  const next = () => setStep((s) => s + 1);

  const saveBio = async () => { if (await save({ business_bio: bio.trim() || null })) next(); };
  const saveLinks = async () => {
    const w = website.trim();
    if (w && !/^https?:\/\/[^\s.]+\.[^\s]+$/i.test(w)) { setWebsiteError("Enter a full address, including https://"); return; }
    const ig = instagram.trim().replace(/^@/, "");
    if (await save({ website: w || null, instagram_handle: ig || null })) next();
  };
  const finish = async (to: string) => {
    await save({ business_onboarding_completed_at: new Date().toISOString(), onboarding_completed_at: new Date().toISOString(), onboarding_tour_seen: true });
    onDone();
    navigate(to, { replace: true });
  };

  const BackLink = () => (
    <button onClick={() => setStep((s) => s - 1)} className="min-h-11 text-sm text-muted-foreground underline">Back</button>
  );

  const Skip = () => (
    <button onClick={next} className="min-h-11 text-sm text-muted-foreground underline">Skip for now</button>
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-background sm:bg-gradient-subtle sm:items-center sm:justify-center sm:p-6 overflow-y-auto"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      role="dialog" aria-modal="true" aria-label="Set up your business"
    >
      <div className="mx-auto w-full max-w-md sm:max-w-xl flex-1 sm:flex-none sm:min-h-[560px] flex flex-col px-5 py-6 sm:p-10 sm:rounded-2xl sm:bg-card sm:shadow-soft sm:border">
        {step < total && (
          <div className="mb-8">
            <p className="text-xs font-medium text-muted-foreground mb-2 text-center">Step {step + 1} of {total}</p>
            <Progress value={((step + 1) / total) * 100} className="h-2" />
          </div>
        )}

        {step === 0 && (
          <div className="flex-1 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Add your business photo or logo</h1>
              <p className="mt-2 text-muted-foreground">Help local families recognise your brand when you post activities, meet-ups or noticeboard updates.</p>
            </div>
            <div className="flex justify-center py-4">
              <ProfilePictureUpload userId={userId} currentAvatarUrl={avatar} firstName={firstName || "Business"} column="business_avatar_url" inline onAvatarUpdate={async (u) => {
                setAvatar(u);
                // Business-only accounts have no separate member photo: use the logo everywhere.
                if (!short) await supabase.from("profiles").update({ avatar_url: u } as any).eq("id", userId);
                invalidateProfiles();
                qc.invalidateQueries({ queryKey: ["user-profile", userId] });
              }} />
            </div>
            <div className="mt-auto space-y-2">
              <Button size="lg" className="w-full" disabled={!avatar} onClick={next}>Continue</Button>
              <Skip />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="flex-1 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Tell local families about your business</h1>
              <p className="mt-2 text-muted-foreground">A sentence or two about what you offer – classes, venue hire, services for babies or toddlers.</p>
            </div>
            <div className="space-y-2">
              <Textarea value={bio} onChange={(e) => setBio(e.target.value)} maxLength={200} rows={4} placeholder="e.g. Baby sensory and messy play classes for ages 0–3 in Balham and Tooting." className="text-base" />
              <p className="text-right text-xs text-muted-foreground">{bio.length}/200</p>
            </div>
            <div className="mt-auto space-y-2">
              <Button size="lg" className="w-full" disabled={saving || !bio.trim()} onClick={saveBio}>Continue</Button>
              <div className="flex items-center justify-between">
                <BackLink />
                <Skip />
              </div>
            </div>
          </div>
        )}

        {step === 2 && !short && (
          <div className="flex-1 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Where can families find you?</h1>
              <p className="mt-2 text-muted-foreground">Where should parents and carers go to book or learn more?</p>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bw-website">Website or booking link</Label>
                <Input id="bw-website" type="url" value={website} onChange={(e) => { setWebsite(e.target.value); setWebsiteError(""); }} placeholder="https://yourwebsite.co.uk" maxLength={500} className="h-12 text-base" />
                {websiteError && <p className="text-sm text-destructive">{websiteError}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="bw-ig">Instagram</Label>
                <Input id="bw-ig" value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="@yourbusiness" maxLength={50} className="h-12 text-base" />
              </div>
            </div>
            <div className="mt-auto space-y-2">
              <Button size="lg" className="w-full" disabled={saving || (!website.trim() && !instagram.trim())} onClick={saveLinks}>Continue</Button>
              <div className="flex items-center justify-between">
                <BackLink />
                <Skip />
              </div>
            </div>
          </div>
        )}

        {step === total && (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
            <div className="text-6xl">🎉</div>
            <h1 className="text-2xl font-bold text-foreground">You're all set, {firstName}!</h1>
            <p className="text-muted-foreground">Welcome to the Balham Babes business community. How would you like to start?</p>
            <div className="w-full space-y-3 mt-4">
              <Button size="lg" className="w-full justify-start gap-3" disabled={saving} onClick={() => finish("/business?tab=listings")}><CalendarPlus className="h-5 w-5" />Add an activity or class</Button>
              <Button size="lg" variant="outline" className="w-full justify-start gap-3" disabled={saving} onClick={() => finish("/noticeboard")}><Megaphone className="h-5 w-5" />Post on the noticeboard</Button>
              <Button size="lg" variant="outline" className="w-full justify-start gap-3" disabled={saving} onClick={() => finish("/business")}><LayoutDashboard className="h-5 w-5" />Go to my business dashboard</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessWelcomeWizard;
